/** EN catalog — FE-2: table contract, clinic setup, catalog, settings. */
export const setupEn: Record<string, string> = {
  'common.save': 'Save changes',
  'common.saved': 'Saved',
  'common.view': 'View',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.enable': 'Enable',
  'common.disable': 'Disable',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.minutes': 'minutes',
  'common.failedKept':
    'The save didn’t go through — nothing you typed was lost. Check the connection and try again.',
  'profile.saveRefused':
    'The clinic system refused this save — nothing you typed was lost. Check the fields and try again.',
  'profile.saveFailed':
    'The clinic system couldn’t complete the save — nothing you typed was lost. Try again in a moment.',

  'table.range': '{from}–{to}',
  'table.rangeOf': '{from}–{to} of {total}',
  'table.perPage': 'Rows per page',
  'table.previous': 'Previous',
  'table.next': 'Next',
  'table.page': 'Page {n}',
  'table.keptNote': 'Your search, filters and sorting stay as they are when you change page.',
  'table.noResultsTitle': 'Nothing matches',
  'table.failedTitle': 'This list couldn’t load',
  'table.failedBody':
    'The clinic system didn’t answer this request, so nothing shown would be current. Nothing you see elsewhere on the page is affected.',
  'table.retry': 'Try again',
  'table.noResultsBody':
    'Check the spelling or clear the search. Nothing has been deleted — there is simply nothing matching these conditions.',

  'setup.title': 'Clinic setup',
  'setup.tabProfile': 'Clinic profile',
  'setup.tabStaff': 'Staff',
  'setup.tabRoles': 'Roles',
  'setup.tabHours': 'Working hours',
  'setup.tabRooms': 'Rooms',
  'setup.tabServices': 'Services',
  'setup.tabCatalogs': 'Clinical catalogue',

  'profile.sub':
    'The clinic’s identity and its clinic-wide configuration — everything else keeps its own surface.',
  'profile.cardProfile': 'Clinic profile',
  'profile.cardProfileMeta': 'The way families know the clinic.',
  'profile.cardConfig': 'Configuration',
  'profile.cardConfigMeta': 'Shapes dates, times and amounts for everyone at this clinic.',
  'profile.editProfile': 'Edit profile',
  'profile.editConfig': 'Edit configuration',
  'profile.displayName': 'Display name',
  'profile.displayNameHelp': 'This name appears in messages and on printed pages.',
  'profile.address': 'Address',
  'profile.addressHelp': 'Used by families and for off-site visits.',
  'profile.contactPhone': 'Contact phone',
  'profile.contactPhoneHelp': 'The number families call.',
  'profile.contactEmail': 'Contact email',
  'profile.website': 'Website',
  'profile.websiteHelp': 'Shown to families. https:// is added if you leave it out.',
  'profile.instagram': 'Instagram',
  'profile.instagramHelp': 'The handle only, with or without @. A pasted profile link works too.',
  'profile.operatingContext': 'Operating context',
  'profile.operatingContextHelp': 'A line or two about what the clinic does.',
  'profile.calendar': 'Calendar system',
  'profile.calendarHelp':
    'Every date in the product is written in this calendar. Changing it asks for confirmation.',
  'profile.jalali': 'Jalali',
  'profile.gregorian': 'Gregorian',
  'profile.currency': 'Currency',
  'profile.currencyHelp':
    'A 3-letter ISO code. IRR is shown across the product as Toman — presentation, never storage.',
  'profile.currencyInvalid': 'Enter a 3-letter ISO code, e.g. IRR.',
  'profile.timezone': 'Timezone',
  'profile.timezoneHelp': 'Times everywhere follow this timezone.',
  'profile.buffer': 'Gap before each session',
  'profile.bufferHelp':
    'Minutes kept free before each session for the practitioner and the room — never for the patient, who may go straight from one session to the next. A practitioner or a room may carry its own value; the larger one applies. Smart Scheduling keeps the same gap.',
  'profile.travel': 'Default travel allowance',
  'profile.travelValue': '{n} minutes each way',
  'profile.travelHelp':
    'Minutes each way, applied to at-home bookings that leave their own travel allowance empty. Whole minutes from {min} to {max}.',
  'profile.travelInvalid': 'Whole minutes between 0 and 480.',
  'profile.scheduleSms': "Evening SMS of tomorrow's schedule to practitioners",
  'profile.scheduleSmsNotice':
    "When this is on, every evening between 18:00 and 21:00 each practitioner receives an SMS with the times of tomorrow's sessions and the patients' full names. Only practitioners who have signed in to the panel at least once with the mobile code receive it.",
  'profile.confirmCalTitle': 'Change the calendar for the whole clinic?',
  'profile.confirmCalBody':
    'Everyone at this clinic will read and enter dates in the {calendar} calendar — in records, scheduling and printed pages.',
  'profile.confirmCalEffect': 'Nothing stored changes — only how dates are written and read.',
  'profile.confirmCalCta': 'Change calendar',
  'profile.readonlyNote':
    'You can read these settings. Changing them belongs to the manage permission — this page simply shows fewer actions, never locked buttons.',
  'profile.saved': 'Clinic settings saved',

  'staff.sub':
    'The staff of this clinic. You create the accounts and hand over the sign-in details yourself.',
  'staff.add': 'Add staff',
  'staff.colMember': 'Staff member',
  'staff.colRoles': 'Roles',
  'staff.colStatus': 'Status',
  'staff.colActions': 'Actions',
  'staff.unnamed': 'Unnamed staff member',
  'staff.active': 'Active',
  'staff.suspended': 'Deactivated',
  'staff.admin': 'Clinic administrator',
  'staff.noRole': 'No role',
  'staff.searchPh': 'Search name or phone',
  'staff.emptyTitle': 'No staff yet',
  'staff.emptyBody':
    'Create the first account and hand over its sign-in details — there is no self-registration.',
  'staff.noDelete':
    'There is no staff deletion in the product — accounts are deactivated and reactivated.',
  'staff.deactivate': 'Deactivate',
  'staff.reactivate': 'Reactivate',
  'staff.deactivateTitle': 'Turn off access for {name}?',
  'staff.deactivateBody':
    'They will no longer be able to sign in to this clinic. This is not deletion, and you can turn it back on.',
  'staff.deactivateEffectTitle': 'What happens',
  'staff.deactivateEffect':
    'Their future bookings and unfinished work stay recorded in their name; patients stay where they are. Reassign work afterwards where needed.',
  'staff.reactivateTitle': 'Turn access back on for {name}?',
  'staff.reactivateBody': 'They can sign in to this clinic again with their existing details.',

  'staffNew.title': 'Create a staff account',
  'staffNew.sub':
    'You create the account, then give the person their sign-in details. There is no self-registration.',
  'staffNew.first': 'First name',
  'staffNew.last': 'Last name',
  'staffNew.mobile': 'Mobile number',
  'staffNew.mobileHelp': 'For sign-in codes and account identity.',
  'staffNew.mobileInvalid': 'Enter a valid mobile number, e.g. 0912 123 4567.',
  'staffNew.errAlreadyMember':
    'This person is already a member of this clinic — nothing new was created. Their roles are managed from their page in the Staff list.',
  'staffNew.errSuspended':
    'This person is already a member of this clinic, currently suspended. Resume their membership from the Staff list instead of creating a new account.',
  'staffNew.errProvisioning':
    'An account with this mobile is still being set up. Try again in a moment — if it keeps failing, contact Zoyaar support.',
  'staffNew.errDisabled':
    'An account with this mobile exists but is turned off. Contact Zoyaar support to restore it.',
  'staffNew.errNationalId':
    'This mobile belongs to an account with a different national ID. Verify the person’s details, or contact Zoyaar support.',
  'staffNew.errRefused':
    'The clinic system refused these details — check each field and try again. Nothing you typed was lost.',
  'staffNew.email': 'Email',
  'staffNew.emailHelp': 'For email-and-password sign-in.',
  'staffNew.role': 'Role at this clinic',
  'staffNew.roleHelp': 'The role decides what they see and may do.',
  'staffNew.cta': 'Create the account',
  'staffNew.pwNote':
    'You never type a password. Creating the account generates a temporary one, shown once on the next screen; afterwards it cannot be viewed again.',
  'staffNew.confirmTitle': 'This mobile number already has an account',
  'staffNew.confirmBody': 'The owner of this number is registered in Zoyaar as:',
  'staffNew.confirmBodyNoName':
    'The owner of this number has no name on record. Continue only if you are sure the number is right.',
  'staffNew.confirmYes': 'Yes, add this person',
  'staffNew.confirmNo': 'No, I will fix the number',
  'staffNew.existing':
    'This mobile number already belongs to an account — it was attached to this clinic with the chosen role. No new password exists to hand over.',

  'handover.title': 'The account is ready',
  'handover.sub':
    'Hand these details over yourself — in person, by phone, or however your clinic does it.',
  'handover.warnOnce':
    'The temporary password is shown only now. Once you leave this page it cannot be shown again.',
  'handover.username': 'Username',
  'handover.mobile': 'Mobile number',
  'handover.email': 'Sign-in email',
  'handover.temp': 'Temporary password',
  'handover.copy': 'Copy',
  'handover.copied': 'Copied',
  'handover.firstLogin':
    'When they first sign in with this temporary password, they are asked to choose their own, and sign-in completes in that same step.',
  'handover.done': 'Done, I’ve handed it over',

  'member.identity': 'Identity and sign-in',
  'member.practitioner': 'Practitioner details',
  'member.practitionerSub': 'Only for roles that deliver sessions. All of it is optional.',
  'member.jobTitle': 'Job title',
  'member.specialty': 'Specialty',
  'member.license': 'License number',
  'member.experience': 'Experience',
  'member.qualified': 'Qualified to assess',
  'member.qualifiedHelp': 'May run initial assessments — affects who can sign an assessment.',
  'member.bio': 'Short bio',
  'member.buffer': 'Gap before each session',
  'member.bufferHelp':
    'Left empty, the clinic default applies. Minutes kept free before each of this practitioner’s sessions.',
  'member.maxDay': 'Max sessions per day',
  'member.maxWeek': 'Max sessions per week',
  'member.rolesCard': 'Roles of this person',
  'member.extraTitle': 'Extra permissions for this person',
  'member.extraSub':
    'The role stays untouched — this page only adds. To reduce what someone can do, change their role.',
  'member.extraAdded': 'added',
  'perm.loading': '…',
  'perm.retired': 'Retired permission (no longer in the catalogue)',

  /* ZOY-635: a short explanation per permission for the clinic manager (names come from the catalogue). */
  'perm.clinic.members.manage.desc': 'Add colleagues to the clinic or remove them.',
  'perm.clinic.roles.manage.desc':
    'Create, edit and deactivate roles, and set what each role may do.',
  'perm.clinic.grants.manage.desc':
    'Give a colleague a role, and add or remove a personal extra permission.',
  'perm.clinic.admin.full.desc':
    'Unrestricted authority in the clinic; granted and revoked by Zoyaar staff only.',
  'perm.clinic.families.manage.desc':
    'Invite a family, accept or decline a family’s request, and end a connection.',
  'perm.clinic.families.read.desc':
    'See invitations, requests and family connections with the clinic.',
  'perm.clinic.audit.read.desc': 'See who did what in the clinic, and when.',
  'perm.clinic.settings.read.desc':
    'See the clinic profile and configuration (calendar, currency, scheduling defaults).',
  'perm.clinic.settings.manage.desc':
    'Change the clinic profile and configuration; every change is recorded.',
  'perm.clinic.personnel.read.desc': 'See colleagues’ and practitioners’ professional details.',
  'perm.clinic.personnel.manage.desc':
    'Record and edit colleagues’ and practitioners’ professional details, including assessment eligibility.',
  'perm.clinic.catalog.read.desc':
    'See the clinic’s services and disciplines and the baseline catalogues.',
  'perm.clinic.catalog.manage.desc':
    'Add clinic-specific services, disable services, and set service prices.',
  'perm.clinic.rooms.read.desc': 'See rooms and their scheduling settings.',
  'perm.clinic.rooms.manage.desc':
    'Create and edit rooms, make them bookable or not, and set the gap before each session.',
  'perm.clinic.capabilities.read.desc':
    'See which services each practitioner and each room can provide.',
  'perm.clinic.capabilities.manage.desc':
    'Set which services each practitioner and each room provides.',
  'perm.clinic.availability.read.desc': 'See working hours, time off and room blockouts.',
  'perm.clinic.availability.manage.desc': 'Set working hours, time off and room blockouts.',
  'perm.clinic.patients.read.desc': 'See and search patient records.',
  'perm.clinic.patients.register.desc':
    'Create a new patient record and complete its registration.',
  'perm.clinic.patients.update.desc':
    'Correct a patient’s identity details; the previous value stays in the history.',
  'perm.clinic.patients.contacts.read.desc':
    'See caregivers and contact details on patient records.',
  'perm.clinic.patients.contacts.manage.desc':
    'Add, edit or remove caregivers on a patient record.',
  'perm.clinic.patients.duplicates.resolve.desc':
    'Decide, when two records look alike, whether they are the same person.',
  'perm.clinic.patients.history.read.desc':
    'See who created or corrected a record and how look-alike records were decided.',
  'perm.clinic.intake.read.desc': 'See patients’ intake records.',
  'perm.clinic.intake.write.desc': 'Write and edit intake records.',
  'perm.clinic.intake.complete.desc': 'Mark an intake as complete.',
  'perm.clinic.assessments.read.desc': 'See assessments and reassessments.',
  'perm.clinic.assessments.write.desc': 'Write and edit assessments and reassessments.',
  'perm.clinic.assessments.complete.desc':
    'Sign and complete an assessment. A practitioner’s assessment eligibility is set separately on their profile.',
  'perm.clinic.packages.read.desc':
    'See package templates and patients’ packages, with prices and planned sessions.',
  'perm.clinic.packages.manage.desc':
    'Create package templates and define or edit a patient’s package: composition, price and discount.',
  'perm.clinic.bookings.read.desc': 'See bookings and the clinic calendar.',
  'perm.clinic.bookings.schedule.desc':
    'Book an appointment for a patient: practitioner, room and time.',
  'perm.clinic.bookings.reschedule.desc': 'Move a booking to another time, practitioner or room.',
  'perm.clinic.bookings.cancel.desc': 'Cancel a booking.',
  'perm.clinic.sessions.read.desc': 'See delivered sessions and the progress derived from them.',
  'perm.clinic.sessions.deliver.desc':
    'Record a session as delivered — the record of what happened.',
  'perm.clinic.sessions.correct.desc':
    'Void or reinstate a session record; every correction stays in the history with its reason.',
  'perm.clinic.sessions.payment.record.desc':
    'Record a session’s payment amount, status and method. Not accounting, settlement or invoicing.',
  'perm.clinic.instruments.read.desc':
    'See a patient’s standardized questionnaires and tests and their results.',
  'perm.clinic.instruments.administer.desc':
    'Start a standardized questionnaire or test for a patient and fill in the draft.',
  'perm.clinic.instruments.finalize.desc':
    'Finalize an administration and issue a correcting one when needed.',
  'perm.clinic.plan.read.desc':
    'See the care plan of patients with an active connection to the clinic.',
  'perm.clinic.plan.author.desc':
    'Write the care plan and home tasks of patients with an active connection, and record progress.',
  'field.digits': 'Digits only.',
  'field.whole': 'A whole number, no decimals.',
  'field.range': 'Between {min, number} and {max, number}.',
  'field.fixFirst': 'Fix the marked field first.',
  'member.passwordKey': 'Password',
  'member.passwordBody':
    'The person manages their own password: a temporary one is replaced at first sign-in, and a forgotten one is recovered from the sign-in page with their email and a code texted to their registered mobile. Nothing about it is set from here.',
  'member.hoursLink': 'Working hours & time off',
  'member.tab.profile': 'Profile',
  'member.tab.hours': 'Working hours',
  'member.tab.timeoff': 'Time off',
  'member.hoursSub':
    'This person’s repeating weekly pattern. A day can hold several ranges, and each range says where the work happens.',
  'member.timeOffSub':
    'Absences that override the weekly pattern. Bookings inside them become reception follow-up work — nothing is cancelled automatically.',
  'member.noPractitionerTitle': 'No practitioner details yet',
  'member.noPractitionerBody':
    'Schedules and time off attach to practitioner details. Save them on the Profile tab first — the job title is enough.',
  'member.onlyClinic':
    'This page shows nothing about any other clinic, and can change nothing there.',

  'roles.sub':
    'Roles say what a person sees and may do. Your clinic can create its own; full clinic administration is protected platform authority.',
  'roles.create': 'Create role',
  'roles.people': '{n, plural, one {# person} other {# people}}',
  'roles.inactive': 'Inactive',
  'roles.activeChip': 'Active',
  'roles.lifecycle': 'Status',
  'roles.deactivate': 'Deactivate this role',
  'roles.deactivateHint': 'Deactivating takes the role out of use without deleting anything.',
  'roles.deactivateTitle': 'Deactivate “{name}”?',
  'roles.deactivateBody':
    'Everyone holding this role immediately loses the permissions it carries. Their assignments are kept — nobody is removed from the clinic — and reactivating restores exactly what they had.',
  'roles.deactivateEffect': 'The role disappears from assignment lists until it is reactivated.',
  'roles.deactivateCta': 'Deactivate the role',
  'roles.reactivate': 'Reactivate this role',
  'roles.reactivateBody':
    'Inactive: it grants nothing and cannot be assigned. Reactivating restores what its holders had.',
  'roles.inactiveNote':
    'This role is inactive. It grants nothing and cannot be assigned; held assignments are kept, and reactivating restores them.',
  'roles.errDuplicate':
    'A role with this name already exists in this clinic — nothing was changed. Pick another name.',
  'roles.errForbidden':
    'The clinic system refused this change. A role can only include permissions you hold yourself.',
  'roles.errRefused':
    'The clinic system refused this change — nothing you typed was lost. Check the name and permissions and try again.',
  'roles.protectedTitle': 'Clinic administrator',
  'roles.protectedBody':
    'Full authority in this clinic — a platform-governed direct grant, not a role. It cannot be created or reduced here.',
  'roles.protectedChip': 'Protected',
  'roles.catalogNote':
    'The permission catalogue and defaults come from the clinic system — the rows below render whatever it defines; nothing is invented here.',
  'roles.name': 'Role name',
  'roles.nameHelp': 'Whatever your clinic calls it.',
  'roles.description': 'Description',
  'roles.copyFrom': 'Start from an existing role',
  'roles.copyFromHelp':
    'Optional — copies that role’s permissions as a starting point, still editable.',
  'roles.copyNone': 'Start empty',
  'roles.createCta': 'Create the role',
  'roles.permissions': 'Permissions',
  'roles.affects': 'A change here affects everyone holding the role.',
  'roles.saveCta': 'Save permissions',

  'hours.sub':
    'A repeating weekly pattern per practitioner. A day can hold several ranges, and each range says where the work happens.',
  'hours.pick': 'Practitioner',
  'hours.noPractitioners': 'No one has practitioner details yet, so there are no schedules to set.',
  'hours.noPractitionersLink': 'Add practitioner details on the staff page.',
  'hours.needsPersonnel':
    'Listing practitioners needs the personnel-profiles permission. Ask a clinic administrator to add it to your role, or to look up a schedule for you.',
  'hours.overlap': 'That range overlaps one already set for this day.',
  'hours.addRange': 'Add a range',
  'hours.day': 'Day',
  'hours.from': 'From',
  'hours.to': 'To',
  'hours.where': 'Where',
  'hours.inClinic': 'In clinic',
  'hours.offSite': 'Off site',
  'hours.both': 'Both',
  'hours.online': 'Online',
  'hours.modesHelp': 'Every mode the practitioner works in during this range — at least one.',
  'hours.presetOnline': 'Also open for online sessions',
  'hours.closed': 'Closed',
  'hours.adminOnly':
    'In V1 the clinic administrator sets working hours and records time off; there is no practitioner request-and-approve flow.',
  'hours.presetsTitle': 'Fill the week with a preset',
  'hours.presetsSub':
    'A shortcut only. It replaces the current schedule with the pattern you pick, and every day stays editable afterwards.',
  'hours.preset.all_week': 'All week',
  'hours.preset.even_days': 'Even days',
  'hours.preset.odd_days': 'Odd days',
  'hours.presetEditable': 'Every day below can still be changed or removed by hand.',
  'hours.presetConfirmTitle': 'Replace the schedule with “{name}”?',
  'hours.presetConfirmBody':
    '{days} · {hours} · {where}. Each of these days gets one range; every other day becomes closed.',
  'hours.presetReplaces':
    '{n, plural, one {The # range currently set is removed first} other {The # ranges currently set are removed first}}. That cannot be undone, but every day can be set again by hand.',
  'hours.presetEmptyNow': 'Nothing is set yet — the preset simply fills the week.',
  'hours.presetCta': 'Replace the schedule',
  'hours.presetDone':
    'Schedule set: {n, plural, one {# day} other {# days}} at {hours}, {where}. Adjust any day below.',
  'hours.presetPartial':
    'Applied with problems — {failed}. The rest of the week is set as shown below.',
  'hours.presetRemoveFailed':
    '{n, plural, one {# old range} other {# old ranges}} could not be removed',
  'hours.presetAddFailed': '{day} could not be added ({reason})',
  'hours.change': 'Change',
  'hours.changeTitle': 'Change this range',
  'hours.changeCta': 'Save the change',
  'hours.changed': 'Range changed.',
  'hours.changeRefused': 'Nothing changed — the new range was refused: {reason}',
  'hours.changeRestoreFailed':
    'The new range was refused ({reason}) and the old one could not be put back. Add it again by hand: {old}.',
  'hours.removeFailed': 'The range could not be removed — nothing changed.',
  'hours.timeOffNone': 'No time off recorded.',
  'hours.timeOff': 'Time off',
  'hours.recordTimeOff': 'Record time off',
  'hours.startsDate': 'From — date',
  'hours.startsTime': 'From — time',
  'hours.endsDate': 'To — date',
  'hours.endsTime': 'To — time',
  'hours.offOrder': 'The end must come after the start — check the dates and times.',
  'hours.reason': 'Reason',
  'hours.reasonHelp': 'Optional, and only clinic administrators see it.',
  'hours.timeOffNote':
    'Time off always saves. Any bookings inside it become reception follow-up work — nothing is cancelled automatically.',
  'hours.saveCta': 'Save time off',
  'hours.timeFormat': '24-hour time, e.g. 14:30',

  'rooms.sub': 'The clinic’s real rooms, and which in-clinic services each one can host.',
  'rooms.add': 'Add room',
  'rooms.colRoom': 'Room',
  'rooms.colType': 'Type',
  'rooms.colStatus': 'Status',
  'rooms.noCode': 'No code',
  'rooms.noType': 'No type',
  'rooms.noDelete':
    'A room you stop using is disabled; its past bookings stay intact. There is no delete in the product.',
  'rooms.name': 'Name',
  'rooms.code': 'Code',
  'rooms.codeHelp': 'Short, for the calendar and for how staff talk.',
  'rooms.type': 'Room type',
  'rooms.typeHelp': 'Free text — whatever your clinic calls this kind of room.',
  'rooms.buffer': 'Gap before each session',
  'rooms.bufferHelp':
    'Left empty, the clinic default applies. When a session is booked in this room, the larger of this and the practitioner’s gap is kept free before it.',
  'rooms.description': 'Description',
  'rooms.available': 'Available for booking',
  'rooms.availableHelp':
    'Turning this off changes no past booking; no new future booking is placed here.',
  'rooms.supported': 'Supported services',
  'rooms.supportedHelp':
    'Only in-clinic services matter here — online and at-home services never take a room.',
  'rooms.createCta': 'Create the room',
  'rooms.emptyTitle': 'No rooms yet',
  'rooms.emptyBody': 'Add the rooms sessions happen in. Suitability for services is set per room.',
  // ZOY-636: room hours and blockouts (availability.manage, not rooms.manage).
  'rooms.hoursTitle': 'Hours',
  'rooms.hoursSub':
    'Left empty, the room is open whenever the clinic books it. Set hours only to close the room outside them; capability, blockouts and the bookable switch always apply.',
  'rooms.hoursOpen': 'No hours set — open by hours.',
  'rooms.hoursNeedManage':
    'Changing a room’s hours or blockouts needs the manage-availability permission.',
  'rooms.blockoutsTitle': 'Blockouts',
  'rooms.blockoutsSub':
    'A room closed for a while — maintenance, an event, a repair. Bookings inside it are refused.',
  'rooms.blockoutsNone': 'No blockout recorded.',
  'rooms.recordBlockout': 'Record a blockout',
  'rooms.blockoutCta': 'Save the blockout',
  'rooms.blockoutNote':
    'A blockout always saves. Any bookings inside it become reception follow-up work — nothing is cancelled automatically.',

  'svc.sub':
    'What the clinic offers: the platform catalogue plus services this clinic created. What is enabled here is what the calendar can book.',
  'svc.add': 'New service',
  'svc.colService': 'Service',
  'svc.colLocation': 'Delivery',
  'svc.colPrice': 'Price',
  'svc.colDuration': 'Default duration',
  'svc.colStatus': 'Status',
  'svc.durationNote': 'a suggestion, not fixed',
  'svc.platform': 'Zoyaar catalogue',
  'svc.clinicMade': 'Created by this clinic',
  'svc.disabledHere': 'Disabled at this clinic',
  'svc.loc.in_clinic': 'In clinic',
  'svc.loc.off_site': 'Off site',
  'svc.loc.both': 'In clinic & off site',
  // ZOY-237: the mode set replaces the single designation.
  'svc.mode.in_clinic': 'In clinic',
  'svc.mode.off_site': 'At home',
  'svc.mode.online': 'Online',
  'svc.modesNeeded': 'Enable at least one delivery mode.',
  'svc.groupCapacity': 'Default group capacity',
  'svc.groupCapacityHelp':
    'Prefills a new group session; up to 20 in clinic, 15 online. Leave empty to set it on every session.',
  'svc.groupNote':
    'A group service runs as one session with several attendees, created from the calendar as a group session.',
  'svc.name': 'Name',
  'svc.description': 'Description',
  'svc.descriptionHelp': 'Shown in the list and on the booking form; short, in family words.',
  'svc.price': 'Price',
  'svc.priceHelp': 'A recorded fact for packages and payment records — not a payment system.',
  'svc.duration': 'Default duration',
  'svc.durationHelp': 'Prefills the booking form; any booking can run shorter or longer.',
  'svc.location': 'Delivery',
  'svc.locationHelp':
    'Every mode this service may be booked in — at least one. A group service is in clinic or online, never at home.',
  'svc.createCta': 'Create the service',
  'svc.oneSwitchNote':
    'One status control: “Enabled at this clinic”. Disabling stops new bookings and cancels nothing existing.',
  'svc.editGapNote':
    'Editing a service’s name and description waits on the catalogue-identity decision in the clinic system — the fields are read-only until it lands.',
  'svc.practitioners': 'Practitioners',
  'svc.practitionersSub':
    'Capability is the permission: without it, a practitioner is never offered for this service. Match and preferred only order Smart Scheduling’s suggestions.',
  'svc.rooms': 'Rooms',
  'svc.roomsSub': 'A room only matters when the session is in clinic.',
  'svc.rankNote': 'ordering only — not permission',
  'svc.preferred': 'Preferred',
  'svc.match': 'Match {n}',
  'svc.grant': 'Add practitioner',
  'svc.removeCapTitle': 'Remove this capability?',
  'svc.removeCapBody':
    'Nothing already booked is cancelled — but if a future booking is later rescheduled, this practitioner may no longer be offered for it.',
  'svc.removeRoomTitle': 'Remove this room’s support?',
  'svc.removeRoomBody':
    'Nothing already booked is cancelled — a future in-clinic booking in this room may need another room when rescheduled.',
  'svc.supports': 'Supports it',
  'svc.notSetUp': 'Not set up for it',
  'svc.emptyTitle': 'No services yet',
  'svc.emptyBody':
    'The platform catalogue appears here once seeded; the clinic can also create its own services.',

  'cat.sub':
    'Shared clinical vocabularies: the platform baseline plus this clinic’s additions. Disabling is clinic-local and hides an entry from new use only.',
  'cat.medications': 'Medications',
  'cat.historyItems': 'History items',
  'cat.add': 'Add entry',
  'cat.category': 'Category',
  'cat.categoryHelp': 'Grouping, e.g. birth, developmental, medical.',
  'cat.name': 'Name',
  'cat.emptyTitle': 'Nothing here yet',
  'cat.emptyBody':
    'Platform baseline entries appear once seeded; clinic additions are created here.',
  /* Clinical workflow classification (ZOY-542/550). */
  'svc.workflow': 'Workflow model',
  'svc.workflowHelp':
    'The standardized clinical meaning of this service. Chosen once and never editable afterwards — a wrongly classified service is replaced by a new entry, not corrected.',
  'svc.workflowNeeded': 'Choose the workflow model first.',
  'svc.wf.assessment': 'Assessment',
  'svc.wf.speech_therapy_session': 'Speech therapy session',
  'svc.wf.occupational_therapy_session': 'Occupational therapy session',
  'svc.wf.consultation': 'Consultation',
  'svc.wf.virtual_reality_session': 'Virtual Reality (VR)',
  'svc.wf.neurotherapy_session': 'Neurotherapy',
  'svc.wf.group_session': 'Group session',
  'svc.wf.unclassified': 'Unclassified',
  'svc.createFailed':
    'The service was not created — nothing was saved. Check the fields and try again.',
  /* Clinical catalogues — Intake redesign (ZOY-574) */
  'cat.complaints': 'Complaints',
  'cat.section': 'Section',
  'cat.sectionHelp': 'Which intake section offers this item.',
  'cat.medPlatformNote':
    'The medication list is managed by the platform. Your clinic can hide or show a row here, but cannot add one — an unlisted medication is entered by name on the intake itself.',
  'cat.archived': 'Archived',
  'cat.createFailed': 'The item was not added — nothing was saved. Check the fields and try again.',
  // ZOY-776: extra-charge kinds.
  'setup.tabExtraCharges': 'Extra charges',
  'xk.sub':
    'What reception may charge beside a session: a family consultation per 15 minutes, a teaching aid, an overrun. An empty list hides the control at payment time. Kinds are switched off, never deleted.',
  'xk.name': 'Name',
  'xk.pricing': 'Pricing',
  'xk.pricing.time': 'By time — rate per 15 minutes',
  'xk.pricing.fixed': 'Fixed amount with a default',
  'xk.pricing.free': 'Free amount — typed each time',
  'xk.unitTime': 'Rate per 15 minutes',
  'xk.unitFixed': 'Default amount',
  'xk.counts': 'Counts as practitioner working time',
  'xk.countsHelp':
    'Minutes of this kind join the practitioner’s working time in the management report.',
  'xk.sortOrder': 'Order in the list',
  'xk.add': 'Add a kind',
  'xk.emptyTitle': 'No kinds yet',
  'xk.emptyBody': 'Add the first kind and “Add an extra” appears when a session is paid.',
  'xk.disabled': 'Off',
  'xk.enable': 'Switch on',
  'xk.disable': 'Switch off',
  'xk.edit': 'Edit',
  'xk.saveFailed': 'Not saved — nothing changed. Check the fields and try again.',
  'xk.errUnit': 'Time and fixed kinds need an amount greater than zero; a free kind has none.',
  'xk.pricingLocked':
    'The pricing model cannot change after creation; the rate and the name can — earlier charges keep their amounts.',
  'xk.countsOn': 'Yes',
  'xk.countsOff': 'No',
};
